/**
 * API client — thin wrapper around fetch.
 * All requests go to /api/* which Vite proxies to the Worker in dev,
 * and Cloudflare routes to the Worker in production.
 */

const BASE = '/api'

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error((err as any).error ?? 'Request failed')
  }

  return res.json()
}

// -----------------------------------------------
// Foods
// -----------------------------------------------

export const api = {
  foods: {
    search: (q: string) =>
      request<any[]>(`/foods/search?q=${encodeURIComponent(q)}`),

    byBarcode: (barcode: string) =>
      request<any>(`/foods/barcode/${barcode}`),

    byId: (id: string) =>
      request<any>(`/foods/${id}`),

    create: (food: Record<string, unknown>) =>
      request<{ id: string }>('/foods', { method: 'POST', body: JSON.stringify(food) }),
  },

  // -----------------------------------------------
  // Logs
  // -----------------------------------------------

  logs: {
    food: {
      list: (date: string) =>
        request<any[]>(`/logs/food?date=${date}`),

      add: (entry: {
        log_date: string
        meal_type: string
        food_item_id: string
        servings: number
      }) => request<{ id: string }>('/logs/food', { method: 'POST', body: JSON.stringify(entry) }),

      remove: (id: string) =>
        request<{ ok: boolean }>(`/logs/food/${id}`, { method: 'DELETE' }),

      copy: (payload: {
        source_date: string
        source_meal: string
        target_date: string
        target_meal: string
      }) =>
        request<{ copied: number }>('/logs/food/copy', {
          method: 'POST',
          body: JSON.stringify(payload),
        }),
    },

    weight: {
      list: (days?: number) =>
        request<any[]>(`/logs/weight${days ? `?days=${days}` : ''}`),

      add: (entry: { log_date: string; weight_kg: number }) =>
        request<{ id: string }>('/logs/weight', { method: 'POST', body: JSON.stringify(entry) }),
    },

    water: {
      get: (date: string) =>
        request<{ total_ml: number }>(`/logs/water?date=${date}`),

      add: (entry: { log_date: string; amount_ml: number }) =>
        request<{ id: string }>('/logs/water', { method: 'POST', body: JSON.stringify(entry) }),
    },

    exercise: {
      list: (date: string) =>
        request<any[]>(`/logs/exercise?date=${date}`),

      add: (entry: {
        log_date: string
        activity: string
        duration_min?: number
        calories_burned?: number
        notes?: string
      }) =>
        request<{ id: string }>('/logs/exercise', { method: 'POST', body: JSON.stringify(entry) }),
    },
  },

  // -----------------------------------------------
  // Summary
  // -----------------------------------------------

  summary: {
    day: (date: string) => request<any>(`/summary/day/${date}`),
    week: (end?: string, days?: number) => {
      const params = new URLSearchParams()
      if (end) params.set('end', end)
      if (days) params.set('days', String(days))
      const qs = params.toString()
      return request<any[]>(`/summary/week${qs ? `?${qs}` : ''}`)
    },
  },

  // -----------------------------------------------
  // Profile
  // -----------------------------------------------

  profile: {
    get: () => request<any>('/profile'),
    update: (data: Record<string, unknown>) =>
      request<any>('/profile', { method: 'PUT', body: JSON.stringify(data) }),
  },

  // -----------------------------------------------
  // Favourites + recents
  // -----------------------------------------------

  favourites: {
    list: () => request<any[]>('/favourites'),
    add: (food_item_id: string) =>
      request<{ ok: boolean }>('/favourites', {
        method: 'POST',
        body: JSON.stringify({ food_item_id }),
      }),
    remove: (foodItemId: string) =>
      request<{ ok: boolean }>(`/favourites/${foodItemId}`, { method: 'DELETE' }),
    recent: () => request<any[]>('/favourites/recent'),
  },

  // -----------------------------------------------
  // Export
  // -----------------------------------------------

  export: {
    csv: (from?: string, to?: string) => {
      const params = new URLSearchParams()
      if (from) params.set('from', from)
      if (to) params.set('to', to)
      // Direct browser download — not a fetch call
      window.location.href = `/api/export/csv?${params.toString()}`
    },
  },

  // -----------------------------------------------
  // Auth
  // -----------------------------------------------

  auth: {
    me: () => request<{ email: string }>('/auth/me'),
  },
}
